import * as Comlink from "comlink";
import type { RecognitionOptions, RecognitionProgress, RecognitionResult } from "../core/types";
import { prepareImage } from "../core/image";

type WorkerResult = Omit<RecognitionResult, "sourceUrl" | "sourceBlob" | "previewBlob">;
interface RecognitionWorkerApi {
  recognize(image: ImageBitmap, options: RecognitionOptions, progress: (value: RecognitionProgress) => void): Promise<WorkerResult>;
  cancel(): void;
}

let worker: Worker | null = null;
let api: Comlink.Remote<RecognitionWorkerApi> | null = null;

function getApi() {
  if (!worker) {
    worker = new Worker(new URL("./recognition.worker.ts", import.meta.url), { type: "module" });
    api = Comlink.wrap<RecognitionWorkerApi>(worker);
  }
  return api!;
}

export async function recognizeFile(
  file: File,
  options: RecognitionOptions,
  onProgress: (progress: RecognitionProgress) => void,
  signal?: AbortSignal
): Promise<RecognitionResult> {
  const prepared = await prepareImage(file);
  const remote = getApi();
  const cancel = () => { void remote.cancel(); };
  signal?.addEventListener("abort", cancel, { once: true });
  try {
    const result = await remote.recognize(Comlink.transfer(prepared.bitmap, [prepared.bitmap]), options, Comlink.proxy(onProgress));
    return {
      ...result,
      sourceBlob: prepared.sourceBlob,
      previewBlob: prepared.previewBlob,
      sourceUrl: URL.createObjectURL(prepared.sourceBlob)
    };
  } finally {
    signal?.removeEventListener("abort", cancel);
  }
}
